<template>
    <div :id="modalName">
        <b-modal :id="modalName" title="Charge Credit Card" scrollable hide-footer size="xl">
            <template v-slot:modal-title>
                <span>Charge Credit Card</span>
            </template>

            <b-container fluid>
                <!-- Section 1: Charge Credit Card Form -->
                <b-row class="mb-4">
                    <b-col>
                        <b-form @submit.stop.prevent="chargeCreditCard">
                            <b-row>
                                <b-col cols="6">
                                    <b-form-group
                                        id="input-group-customer"
                                        :label="isCustomerLocked ? 'Customer Account (from sales order):' : 'Customer Account:'"
                                        label-for="input-customer"
                                        label-size="lg"
                                        class="form-label-bold"
                                        :label-style="{ fontWeight: 'bold', fontSize: '1.1em' }"
                                    >
                                        <div class="options-assign" v-if="!isCustomerLocked">
                                            <div class="autosuggest-container">
                                                <vue-autosuggest 
                                                    v-model="form.customerAccount"
                                                    :suggestions="autosuggest.customer.suggestions" 
                                                    @click="clickHandlerCustomer"
                                                    @input="fetchResultsCustomer" 
                                                    @selected="onSelectedCustomer"
                                                    @keydown.tab="suggestFirstCustomer($event.target.value)"
                                                    :get-suggestion-value="getSuggestionValueCustomer"
                                                    :input-props="{id:'autosuggest__input', placeholder:'Search for customer'}"
                                                    @keyup.enter="suggestFirstCustomer($event.target.value)"
                                                    @change="customerChanged(0)">
                                                    <b-container slot-scope="{suggestion}" fluid>
                                                        <b-row>
                                                            <b-col class="text-right">
                                                                {{ suggestion.item.name }} ({{suggestion.item.id}})
                                                            </b-col>
                                                        </b-row>
                                                    </b-container>
                                                </vue-autosuggest>
                                            </div>
                                        </div>
                                        <b-form-input
                                            v-else
                                            id="input-customer"
                                            v-model="form.customerAccount"
                                            type="text"
                                            placeholder="Customer from sales order"
                                            readonly
                                            disabled
                                            class="customer-locked"
                                        ></b-form-input>
                                        <small class="text-muted" v-if="isCustomerLocked">
                                            Customer field is locked because this is from a sales order
                                        </small>
                                    </b-form-group>
                                </b-col>
                                <b-col cols="6">
                                    <b-form-group
                                        id="input-group-amount"
                                        label="Amount to Charge:"
                                        label-for="input-amount"
                                        label-size="lg"
                                        class="form-label-bold"
                                        :label-style="{ fontWeight: 'bold', fontSize: '1.1em' }"
                                    >
                                        <b-input-group>
                                            <b-input-group-prepend>
                                                <b-input-group-text>$</b-input-group-text>
                                            </b-input-group-prepend>
                                            <b-form-input
                                                id="input-amount"
                                                v-model="form.amount"
                                                type="number"
                                                step="0.01"
                                                placeholder="0.00"
                                                required
                                                @blur="formatAmount"
                                            ></b-form-input>
                                        </b-input-group>
                                    </b-form-group>
                                </b-col>
                            </b-row>
                            
                            <!-- Credit Card Section -->
                            <b-row>
                                <b-col>
                                    <!-- Show saved card info if available and not overridden -->
                                    <div v-if="selectedCard && !showCardInputs" class="saved-card-info">
                                        <b-form-group
                                            id="input-group-saved-card"
                                            label="Credit Card:"
                                            label-size="lg"
                                            class="form-label-bold"
                                            :label-style="{ fontWeight: 'bold', fontSize: '1.1em' }"
                                        >
                                            <div class="saved-card-display">
                                                <div class="card-info">
                                                    <i class="fas fa-credit-card text-primary mr-2"></i>
                                                    <span class="card-number">•••• •••• •••• {{ selectedCard.number.slice(-4) }}</span>
                                                    <span class="card-expiry ml-3">Exp: {{ selectedCard.exp }}</span>
                                                    <b-badge v-if="selectedCard.default" variant="primary" class="ml-2">Default</b-badge>
                                                </div>
                                                <b-button 
                                                    variant="outline-secondary" 
                                                    size="sm" 
                                                    @click="useDifferentCard"
                                                    class="mt-2"
                                                >
                                                    Use a different card
                                                </b-button>
                                            </div>
                                        </b-form-group>
                                    </div>
                                    
                                    <!-- Show card input fields when entering new card -->
                                    <div v-if="!selectedCard || showCardInputs" class="new-card-inputs">
                                        <b-row>
                                            <b-col cols="6">
                                                <b-form-group
                                                    id="input-group-card-number"
                                                    label="CC #:"
                                                    label-for="input-card-number"
                                                    label-size="lg"
                                                    class="form-label-bold"
                                                    :label-style="{ fontWeight: 'bold', fontSize: '1.1em' }"
                                                >
                                                    <b-form-input
                                                        id="input-card-number"
                                                        v-model="form.cardNumber"
                                                        type="text"
                                                        placeholder="1234 5678 9012 3456"
                                                        maxlength="19"
                                                        @input="formatCardNumber"
                                                        required
                                                    ></b-form-input>
                                                </b-form-group>
                                            </b-col>
                                            <b-col cols="6">
                                                <b-form-group
                                                    id="input-group-expiry"
                                                    label="Exp:"
                                                    label-for="input-expiry"
                                                    label-size="lg"
                                                    class="form-label-bold"
                                                    :label-style="{ fontWeight: 'bold', fontSize: '1.1em' }"
                                                >
                                                    <b-form-input
                                                        id="input-expiry"
                                                        v-model="form.expiry"
                                                        type="text"
                                                        placeholder="MM/YY"
                                                        maxlength="5"
                                                        @input="formatExpiry"
                                                        required
                                                    ></b-form-input>
                                                </b-form-group>
                                            </b-col>
                                        </b-row>
                                        <b-row>
                                            <b-col cols="4">
                                                <b-form-group
                                                    id="input-group-cvc"
                                                    :label="paymentConfig.cvc_required ? 'CVC:' : 'CVC: (Optional)'"
                                                    label-for="input-cvc"
                                                    label-size="lg"
                                                    class="form-label-bold"
                                                    :label-style="{ fontWeight: 'bold', fontSize: '1.1em' }"
                                                >
                                                    <b-form-input
                                                        id="input-cvc"
                                                        v-model="form.cvc"
                                                        type="text"
                                                        :placeholder="paymentConfig.cvc_required ? 'CVC' : 'CVC (Optional)'"
                                                        maxlength="4"
                                                        :required="paymentConfig.cvc_required"
                                                    ></b-form-input>
                                                </b-form-group>
                                            </b-col>
                                        </b-row>
                                        
                                        <!-- CVC requirement notice -->
                                        <div class="mt-2">
                                            <small class="text-muted">
                                                <i class="fas fa-info-circle"></i> 
                                                CVC is currently {{ paymentConfig.cvc_required ? 'required' : 'optional' }} for payment processing.
                                            </small>
                                        </div>
                                    </div>
                                </b-col>
                            </b-row>
                            
                            <b-row>
                                <b-col>
                                    <b-form-checkbox
                                        id="save-card-checkbox"
                                        v-model="form.saveCardOnFile"
                                        name="save-card-checkbox"
                                    >
                                        Save card on file {{ paymentConfig.cvc_required ? '(CVC required)' : '(CVC optional)' }}
                                    </b-form-checkbox>
                                </b-col>
                            </b-row>
                            <b-row>
                                <b-col class="text-right">
                                    <b-button type="submit" variant="primary" :disabled="isProcessing">
                                        <b-spinner small v-if="isProcessing"></b-spinner>
                                        {{ isProcessing ? 'Processing...' : 'Charge Card' }}
                                    </b-button>
                                </b-col>
                            </b-row>
                        </b-form>
                    </b-col>
                </b-row>

                <!-- Section 2: Sales Orders Selection (only shown when showSalesOrdersSection is true) -->
                <b-row v-if="showSalesOrdersSection" class="mb-4">
                    <b-col>
                        <h5 class="mb-3">Select Sales Orders</h5>
                        <div v-if="!customerId && !form.customerId" class="text-center text-muted mb-3">
                            <p>Please select a customer above to see their unpaid sales orders.</p>
                        </div>
                        <b-table
                            v-if="customerId || form.customerId"
                            :items="salesOrders"
                            :fields="salesOrderFields"
                            striped
                            hover
                            responsive
                        >
                            <template v-slot:head(selected)>
                                <b-form-checkbox
                                    v-model="selectAllSalesOrders"
                                    @change="toggleSelectAllSalesOrders"
                                ></b-form-checkbox>
                            </template>
                            <template v-slot:cell(selected)="row">
                                <b-form-checkbox
                                    v-model="row.item.selected"
                                    @change="updateSelectedSalesOrders"
                                ></b-form-checkbox>
                            </template>
                            <template v-slot:cell(total)="row">
                                ${{ numberWithCommas(row.item.total) }}
                            </template>
                            <template v-slot:cell(balance_due)="row">
                                ${{ numberWithCommas(row.item.balance_due) }}
                            </template>
                        </b-table>
                        <div v-if="customerId || form.customerId" class="text-right">
                            <strong>Total to charge: ${{ numberWithCommas(selectedSalesOrdersTotal) }}</strong>
                        </div>
                    </b-col>
                </b-row>

                <!-- Section 3: Credit Cards on File -->
                <b-row class="mb-4">
                    <b-col>
                        <h5 class="mb-3">Credit Cards on File</h5>
                        <b-table
                            :items="customerCards"
                            :fields="cardFields"
                            striped
                            hover
                            responsive
                            v-if="customerCards.length > 0"
                        >
                            <template v-slot:cell(maskedNumber)="row">
                                <span v-if="$can('view-customer-detail-payment-method-full-card')">
                                    {{ formatCardNumberForDisplay(row.item.number) }}
                                </span>
                                <span v-else>
                                    {{ row.item.maskedNumber }}
                                </span>
                                <b-badge v-if="row.item.default" variant="primary" class="ml-2">Default</b-badge>
                            </template>
                            <template v-slot:cell(expiry)="row">
                                {{ row.item.exp }}
                            </template>
                            <template v-slot:cell(actions)="row">
                                <b-button size="sm" variant="outline-primary" @click="useCardOnFile(row.item)">
                                    Use This Card
                                </b-button>
                            </template>
                        </b-table>
                        <div v-else class="text-center text-muted">
                            <p>No saved cards found for this customer.</p>
                        </div>
                    </b-col>
                </b-row>

                <!-- Section 4: Notes -->
                <b-row>
                    <b-col>
                        <b-form-group
                            id="input-group-notes"
                            label="Notes:"
                            label-for="input-notes"
                            label-size="sm"
                        >
                            <b-form-textarea
                                id="input-notes"
                                v-model="form.notes"
                                placeholder="Enter any additional notes..."
                                rows="3"
                            ></b-form-textarea>
                        </b-form-group>
                    </b-col>
                </b-row>
            </b-container>
        </b-modal>
    </div>
</template>

<script>
import { VueAutosuggest } from "vue-autosuggest"

export default {
    name: 'ChargeCreditCard',
    components: {
        VueAutosuggest
    },
    props: {
        amount: {
            type: [Number, String],
            default: 0
        },
        customerId: {
            type: [Number, String],
            default: null
        },
        orderId: {
            type: [Number, String],
            default: null
        },
        showSalesOrdersSection: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            modalName: 'modal-common-charge-credit-card',
            isProcessing: false,
            customerCards: [],
            salesOrders: [],
            selectedCustomer: null,
            paymentConfig: {
                cvc_required: false
            },
            autosuggest: {
                customer: {
                    suggestions: [],
                    debounceMilliseconds: 1500,
                    timeout: null
                }
            },
            form: {
                customerAccount: '',
                customerId: null,
                amount: this.amount ? parseFloat(this.amount).toFixed(2) : '0.00',
                cardNumber: '',
                expiry: '',
                cvc: '',
                saveCardOnFile: false,
                notes: ''
            },
            cardFields: [
                { key: 'maskedNumber', label: 'Card Number' },
                { key: 'expiry', label: 'Expiry' },
                { key: 'cardholderName', label: 'Cardholder Name' },
                { key: 'actions', label: 'Actions' }
            ],
            salesOrderFields: [
                { key: 'selected', label: 'Select' },
                { key: 'sales_order_number', label: 'Sales Order #' },
                { key: 'date', label: 'Date' },
                { key: 'customer_name', label: 'Customer' },
                { key: 'total', label: 'Total' },
                { key: 'balance_due', label: 'Balance Due' }
            ],
            selectAllSalesOrders: false,
            showCardInputs: false, // New state variable to control card input visibility
            defaultCard: null, // New state variable to store the default card
            selectedCard: null // New state variable to store the currently selected card (either default or manually entered)
        }
    },
    computed: {
        selectedSalesOrdersTotal() {
            return this.salesOrders
                .filter(order => order.selected)
                .reduce((total, order) => total + parseFloat(order.balance_due || order.total), 0)
        },
        isCustomerLocked() {
            // Customer is locked if we have a customerId from props (Sales Order Detail page)
            // For Payments page, customer is always searchable
            return !!this.customerId
        }
    },
    watch: {
        amount(newAmount) {
            this.form.amount = newAmount ? parseFloat(newAmount).toFixed(2) : '0.00'
        },
        customerId(newCustomerId) {
            if (newCustomerId) {
                this.loadCustomerAndCards(newCustomerId)
            }
        },
        orderId(newOrderId) {
            if (newOrderId) {
                this.form.salesOrderNumber = newOrderId
            }
        },
        defaultCard(newDefaultCard) {
            // When the default card changes, update the form if it's the one currently displayed
            if (newDefaultCard && !this.showCardInputs) {
                this.useCardOnFile(newDefaultCard)
            }
        },
        selectedCard(newSelectedCard) {
            // When the selected card changes, update the form if it's the one currently displayed
            if (newSelectedCard && !this.showCardInputs) {
                this.useCardOnFile(newSelectedCard)
            }
        }
    },
    mounted() {
        // Load payment configuration
        this.loadPaymentConfig()
        
        // Only load initial data if customerId is provided (Sales Order Detail page)
        // For Payments page, we don't load anything initially - user must select customer first
        if (this.customerId && !this.showSalesOrdersSection) {
            this.loadCustomerAndCards(this.customerId)
        } else {
        }
        
        // Only load sales orders if we have a customer (from Sales Order Detail)
        // For Payments page, we'll load sales orders when a customer is selected
        if (this.showSalesOrdersSection && this.customerId) {
            this.loadSalesOrders()
        }
    },
    methods: {
        clickHandlerCustomer(item) {
            let query = this.form.customerAccount
            if (!query) {
                this.autosuggest.customer.suggestions = []
            }
        },

        fetchResultsCustomer() {
            const query = this.form.customerAccount

            clearTimeout(this.autosuggest.customer.timeout);
            this.autosuggest.customer.timeout = setTimeout(() => {
                const customersPromise = axios.get('api/lists/customers/getcustomersfilter/' + query);

                Promise.all([customersPromise]).then(values => {
                    this.autosuggest.customer.suggestions = []

                    const customers = values[0].data.items

                    customers.length &&
                    this.autosuggest.customer.suggestions.push({ name: "customers", data: customers })
                })
            }, this.autosuggest.customer.debounceMilliseconds)
        },

        onSelectedCustomer(item) {
            if (item) {
                // Only allow customer selection if no customerId was provided (i.e., from Payments page)
                if (this.customerId) {
                    return // Don't allow changing customer when called from Sales Order Detail
                }
                
                this.selectedCustomer = item.item
                this.form.customerId = item.item.id
                this.loadCustomerAndCards(item.item.id)
                
                // Load sales orders for this customer if we're on the Payments page
                if (this.showSalesOrdersSection) {
                    this.loadSalesOrders(item.item.id)
                }
            }
        },

        suggestFirstCustomer(query) {
            let suggests = this.autosuggest.customer.suggestions
            let arr = suggests[0].data

            if (arr.length) {
                for (var i = 0; i < arr.length; i++) {
                    let cur = arr[i].name.toLowerCase()
                    if (cur.includes(query.toLowerCase())) {
                        this.selectedCustomer = arr[i]
                        this.form.customerId = arr[i].id
                        this.loadCustomerAndCards(arr[i].id)
                        
                        // Load sales orders for this customer if we're on the Payments page
                        if (this.showSalesOrdersSection) {
                            this.loadSalesOrders(arr[i].id)
                        }
                        
                        this.autosuggest.customer.suggestions = []
                        break;
                    } else {
                        continue
                    }
                }
            }
        },

        getSuggestionValueCustomer(suggestion) {
            return suggestion.item.name
        },

        customerChanged(action) {
            // This method is called when customer info changes
            // We can use this for any additional logic if needed
        },

        async loadPaymentConfig() {
            try {
                const response = await axios.get('/api/payments/config')
                if (response.data.success) {
                    this.paymentConfig = response.data.config
                }
            } catch (error) {
                console.warn('Could not load payment configuration, using defaults')
            }
        },

        async loadCustomerAndCards(customerId) {
            try {
                // Load customer details
                const customerResponse = await axios.get(`/api/lists/customers/getcustomer/${customerId}`)
                
                if (customerResponse.data.customer) {
                    this.selectedCustomer = customerResponse.data.customer
                    this.form.customerAccount = customerResponse.data.customer.name
                    this.form.customerId = customerResponse.data.customer.id
                }

                // Load customer cards
                const cardsResponse = await axios.get(`/api/payments/customers/${customerId}/cards`)
                this.customerCards = cardsResponse.data.cards || []

                // Auto-select default card if available
                const defaultCard = this.customerCards.find(card => card.default)
                this.defaultCard = defaultCard // Set the default card
                
                // If there's a default card, automatically select it
                if (defaultCard) {
                    this.selectedCard = defaultCard
                    this.useCardOnFile(defaultCard)
                }
                
                // Load sales orders for this customer if we're on the Payments page
                if (this.showSalesOrdersSection && !this.customerId) {
                    this.loadSalesOrders(customerId)
                }
            } catch (error) {
                this.customerCards = []
            }
        },

        async loadSalesOrders(customerId = null) {
            try {
                const params = {}
                if (customerId) {
                    params.customer_id = customerId
                }
                
                const response = await axios.get('/api/payments/sales-orders/unpaid', { params })
                
                // Ensure we have an array to work with
                const salesOrdersArray = Array.isArray(response.data.sales_orders) ? response.data.sales_orders : []
                this.salesOrders = salesOrdersArray.map(order => ({
                    ...order,
                    selected: false
                }))
            } catch (error) {
                this.salesOrders = []
            }
        },

        useCardOnFile(card) {
            // Set this as the selected card
            this.selectedCard = card
            
            // Pre-fill the form with the selected card
            // Format the card number properly
            this.formatCardNumber(card.number)
            this.form.expiry = card.exp
            // Auto-fill CVC from saved card
            this.form.cvc = card.cvc || ''
            
            // Hide the card input fields since we're using a saved card
            this.showCardInputs = false
        },

        useDifferentCard() {
            this.showCardInputs = true;
            this.form.cardNumber = '';
            this.form.expiry = '';
            this.form.cvc = '';
        },

        toggleSelectAllSalesOrders() {
            this.salesOrders.forEach(order => {
                order.selected = this.selectAllSalesOrders
            })
            this.updateSelectedSalesOrders()
        },

        updateSelectedSalesOrders() {
            const allSelected = this.salesOrders.every(order => order.selected)
            const someSelected = this.salesOrders.some(order => order.selected)
            
            this.selectAllSalesOrders = allSelected
            
            // Update the amount to charge based on selected orders (use balance due, not total)
            if (this.showSalesOrdersSection) {
                this.form.amount = this.selectedSalesOrdersTotal
            }
        },

        formatCardNumber(value) {
            // Remove all non-digits
            let cleaned = value.replace(/\D/g, '')
            
            // Add spaces every 4 digits
            let formatted = cleaned.replace(/(\d{4})(?=\d)/g, '$1 ')
            
            this.form.cardNumber = formatted
        },

        formatExpiry(value) {
            // Remove all non-digits
            let cleaned = value.replace(/\D/g, '')
            
            // Add slash after 2 digits
            if (cleaned.length >= 2) {
                this.form.expiry = cleaned.slice(0, 2) + '/' + cleaned.slice(2, 4)
            } else {
                this.form.expiry = cleaned
            }
        },

        async chargeCreditCard() {
            if (!this.validateForm()) {
                return
            }

            this.isProcessing = true

            try {
                // Prepare the payment data
                let cardNumber, expiryMonth, expiryYear
                
                if (this.selectedCard && !this.showCardInputs) {
                    // Using a saved card
                    cardNumber = this.selectedCard.number.replace(/\s/g, '')
                    expiryMonth = parseInt(this.selectedCard.exp.split('/')[0])
                    expiryYear = parseInt('20' + this.selectedCard.exp.split('/')[1])
                } else {
                    // Using manually entered card
                    cardNumber = this.form.cardNumber.replace(/\s/g, '')
                    expiryMonth = parseInt(this.form.expiry.split('/')[0])
                    expiryYear = parseInt('20' + this.form.expiry.split('/')[1])
                }
                
                const paymentData = {
                    customer_id: this.form.customerId || this.customerId,
                    amount: parseFloat(this.form.amount),
                    card_number: cardNumber,
                    expiry_month: expiryMonth,
                    expiry_year: expiryYear,
                    cvc: String(this.form.cvc), // Convert CVC to string for backend validation
                    cardholder_name: this.selectedCustomer ? this.selectedCustomer.name : (this.form.customerAccount || ''),
                    save_card_on_file: this.form.saveCardOnFile,
                    notes: this.form.notes,
                    selected_sales_orders: this.showSalesOrdersSection ? 
                        this.salesOrders.filter(order => order.selected).map(order => order.id) : 
                        (this.orderId ? [this.orderId] : [])
                }

                // Call the Kaja payments API
                const response = await axios.post('/api/payments/charge-credit-card', paymentData)

                if (response.data.success) {
                    
                    // Emit event to parent to show success confirmation modal
                    this.$emit('showPaymentResult', {
                        title: 'Payment Successful',
                        message: `Payment Approved!`,
                        details: `Amount: $${this.numberWithCommas(this.form.amount)}, Transaction ID: ${response.data.kaja_transaction_id}, Customer: ${this.selectedCustomer ? this.selectedCustomer.name : this.form.customerAccount}`,
                        variant: 'success',
                        icon: 'fas fa-check-circle text-success',
                        buttonText: 'Close'
                    })

                    // Emit event to parent component
                    this.$emit('paymentProcessed', response.data)
                    
                    // Close the payment modal
                    this.$bvModal.hide(this.modalName)
                    
                    // Reset the form for next use
                    this.resetForm()
                } else {
                    throw new Error(response.data.message || 'Payment processing failed')
                }

            } catch (error) {
                // Emit event to parent to show error confirmation modal
                this.$emit('showPaymentResult', {
                    title: 'Payment Failed',
                    message: 'Payment processing failed',
                    details: error.response?.data?.message || error.message || 'An error occurred while processing your payment.',
                    variant: 'danger',
                    icon: 'fas fa-exclamation-triangle text-danger',
                    buttonText: 'Close'
                })
            } finally {
                this.isProcessing = false
            }
        },

        resetForm() {
            this.form = {
                customerAccount: this.customerId ? (this.selectedCustomer ? this.selectedCustomer.name : '') : '',
                customerId: this.customerId || null,
                amount: this.amount ? parseFloat(this.amount).toFixed(2) : '0.00',
                cardNumber: '',
                expiry: '',
                cvc: '',
                saveCardOnFile: false,
                notes: ''
            }
            this.showCardInputs = false // Reset card input visibility
            this.defaultCard = null // Clear default card
            this.selectedCard = null // Clear selected card
        },

        validateForm() {
            if (!this.form.customerId && !this.customerId) {
                this.$emit('showPaymentResult', {
                    title: 'Validation Error',
                    message: 'Please select a customer',
                    details: 'A customer must be selected to process the payment.',
                    variant: 'warning',
                    icon: 'fas fa-exclamation-triangle text-warning',
                    buttonText: 'OK'
                })
                return false
            }

            if (!this.form.amount || parseFloat(this.form.amount) <= 0) {
                this.$emit('showPaymentResult', {
                    title: 'Validation Error',
                    message: 'Please enter a valid amount',
                    details: 'The amount must be greater than $0.00.',
                    variant: 'warning',
                    icon: 'fas fa-exclamation-triangle text-warning',
                    buttonText: 'OK'
                })
                return false
            }

            // If using a saved card, CVC validation depends on configuration
            if (this.selectedCard && !this.showCardInputs) {
                if (this.paymentConfig.cvc_required && !String(this.form.cvc).match(/^\d{3,4}$/)) {
                    this.$emit('showPaymentResult', {
                        title: 'Validation Error',
                        message: 'Please enter a valid CVC',
                        details: 'CVC is required for payment processing.',
                        variant: 'warning',
                        icon: 'fas fa-exclamation-triangle text-warning',
                        buttonText: 'OK'
                    })
                    return false
                }
                return true;
            }

            // If manually entering card details, validate all fields
            if (!this.form.cardNumber.replace(/\s/g, '').match(/^\d{13,19}$/)) {
                this.$emit('showPaymentResult', {
                    title: 'Validation Error',
                    message: 'Please enter a valid card number',
                    details: 'The card number must be between 13 and 19 digits.',
                    variant: 'warning',
                    icon: 'fas fa-exclamation-triangle text-warning',
                    buttonText: 'OK'
                })
                return false
            }

            if (!this.form.expiry.match(/^\d{2}\/\d{2}$/)) {
                this.$emit('showPaymentResult', {
                    title: 'Validation Error',
                    message: 'Please enter a valid expiry date',
                    details: 'The expiry date must be in MM/YY format.',
                    variant: 'warning',
                    icon: 'fas fa-exclamation-triangle text-warning',
                    buttonText: 'OK'
                })
                return false
            }

            // CVC validation for manually entered cards depends on configuration
            if (this.paymentConfig.cvc_required && !String(this.form.cvc).match(/^\d{3,4}$/)) {
                this.$emit('showPaymentResult', {
                    title: 'Validation Error',
                    message: 'Please enter a valid CVC',
                    details: 'CVC is required for payment processing.',
                    variant: 'warning',
                    icon: 'fas fa-exclamation-triangle text-warning',
                    buttonText: 'OK'
                })
                return false
            } else if (this.form.cvc && !String(this.form.cvc).match(/^\d{3,4}$/)) {
                // CVC is optional but validate if provided
                this.$emit('showPaymentResult', {
                    title: 'Validation Error',
                    message: 'Please enter a valid CVC',
                    details: 'The CVC must be 3 or 4 digits, or leave blank.',
                    variant: 'warning',
                    icon: 'fas fa-exclamation-triangle text-warning',
                    buttonText: 'OK'
                })
                return false
            }

            return true
        },

        numberWithCommas(x) {
            return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
        },
        formatCardNumberForDisplay(cardNumber) {
            if (!cardNumber) return '';
            const cleaned = cardNumber.replace(/\D/g, '');
            const formatted = cleaned.replace(/(\d{4})(?=\d)/g, '$1-');
            return formatted.slice(0, 19); // Ensure it doesn't exceed 19 characters
        },

        formatAmount() {
            // Format amount to 2 decimal places
            if (this.form.amount && !isNaN(this.form.amount)) {
                this.form.amount = parseFloat(this.form.amount).toFixed(2);
            }
        }
    }
}
</script>

<style scoped>
.credit-card-form {
    max-width: 800px;
    margin: 0 auto;
}

.card-table {
    margin-top: 20px;
}

.amount-display {
    font-size: 1.2em;
    font-weight: bold;
    color: #28a745;
}

.form-section {
    margin-bottom: 30px;
    padding: 20px;
    border: 1px solid #dee2e6;
    border-radius: 5px;
    background-color: #f8f9fa;
}

.form-section h5 {
    color: #495057;
    margin-bottom: 20px;
}

.customer-locked {
    background-color: #f8f9fa !important;
    color: #6c757d !important;
    cursor: not-allowed !important;
}

.table-disabled {
    opacity: 0.6;
    pointer-events: none;
}

.form-label-bold {
    font-weight: bold !important;
    font-size: 1.1em !important;
}

/* Target Bootstrap-Vue form labels directly */
.form-label-bold label {
    font-weight: bold !important;
    font-size: 1.1em !important;
}

/* Alternative selector for Bootstrap-Vue */
.form-label-bold .col-form-label {
    font-weight: bold !important;
    font-size: 1.1em !important;
}

/* Direct targeting of form labels */
.form-label-bold .form-group > label,
.form-label-bold .form-group label {
    font-weight: bold !important;
    font-size: 1.1em !important;
}

/* Force bold on all labels within form-label-bold containers */
.form-label-bold label,
.form-label-bold .form-label,
.form-label-bold .col-form-label {
    font-weight: bold !important;
    font-size: 1.1em !important;
}

/* More specific selectors to override Bootstrap-Vue */
.form-label-bold .form-group label,
.form-label-bold .form-group > label,
.form-label-bold .form-group .col-form-label {
    font-weight: bold !important;
    font-size: 1.1em !important;
}

/* Target the specific label elements directly */
.form-label-bold .form-group label[for],
.form-label-bold .form-group > label[for] {
    font-weight: bold !important;
    font-size: 1.1em !important;
}

/* Use attribute selectors for maximum specificity */
.form-label-bold .form-group label[for="input-customer"],
.form-label-bold .form-group label[for="input-amount"],
.form-label-bold .form-group label[for="input-card-number"],
.form-label-bold .form-group label[for="input-expiry"],
.form-label-bold .form-label-bold .form-group label[for="input-cvc"],
.form-label-bold .form-group label[for="input-cardholder"] {
    font-weight: bold !important;
    font-size: 1.1em !important;
}

/* Target Bootstrap-Vue specific classes and IDs */
.form-label-bold .col-form-label-lg,
.form-label-bold .d-block.col-form-label-lg,
.form-label-bold label.col-form-label-lg {
    font-weight: bold !important;
    font-size: 1.1em !important;
}

/* Target the specific Bootstrap-Vue label IDs */
.form-label-bold label[id*="__BV_label_"],
.form-label-bold #input-group-customer__BV_label_,
.form-label-bold #input-group-amount__BV_label_,
.form-label-bold #input-group-card-number__BV_label_,
.form-label-bold #input-group-expiry__BV_label_,
.form-label-bold #input-group-cvc__BV_label_,
.form-label-bold #input-group-cardholder__BV_label_ {
    font-weight: bold !important;
    font-size: 1.1em !important;
}

/* Force bold on ALL labels within the modal, regardless of nesting */
#modal-common-charge-credit-card label,
#modal-common-charge-credit-card .col-form-label,
#modal-common-charge-credit-card .col-form-label-lg {
    font-weight: bold !important;
    font-size: 1.1em !important;
}

/* Target form labels specifically by their container */
.form-label-bold .form-group > label,
.form-label-bold .form-group label,
.form-label-bold .form-group .col-form-label,
.form-label-bold .form-group .col-form-label-lg {
    font-weight: bold !important;
    font-size: 1.1em !important;
}

/* Nuclear option - target everything */
#modal-common-charge-credit-card .form-label-bold label,
#modal-common-charge-credit-card .form-label-bold .col-form-label,
#modal-common-charge-credit-card .form-label-bold .col-form-label-lg {
    font-weight: bold !important;
    font-size: 1.1em !important;
}

/* Ultimate override - target by modal ID and force all labels */
#modal-common-charge-credit-card label,
#modal-common-charge-credit-card .col-form-label,
#modal-common-charge-credit-card .col-form-label-lg,
#modal-common-charge-credit-card .form-group label,
#modal-common-charge-credit-card .form-group .col-form-label,
#modal-common-charge-credit-card .form-group .col-form-label-lg {
    font-weight: bold !important;
    font-size: 1.1em !important;
}

/* Force override with maximum specificity */
#modal-common-charge-credit-card .form-label-bold .form-group label,
#modal-common-charge-credit-card .form-label-bold .form-group .col-form-label,
#modal-common-charge-credit-card .form-label-bold .form-group .col-form-label-lg {
    font-weight: bold !important;
    font-size: 1.1em !important;
}

.options-assign {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
}

.options-assign input {
    width: 100%;
    padding: 0.5rem;
    height: calc(1.5em + 0.3rem + 2px);
}

.options-assign ul {
    width: 100%;
    color: rgba(30, 39, 46,1.0);
    list-style: none;
    margin: 0;
    padding: 0.5rem 0 .5rem 0;
}

.options-assign li {
    margin: 0 0 0 0;
    border-radius: 5px;
    padding: 0.75rem 0 0.75rem 0.75rem;
    display: flex;
    align-items: center;
}

.options-assign li:hover {
    cursor: pointer;
}

.options-assign .autosuggest-container {
    display: flex;
    width: 100%;
}

.options-assign .autosuggest-container li {
    white-space: nowrap;
    text-align: left;
}

.options-assign #autosuggest { 
    width: 100%; 
    display: block; 
}

.options-assign .autosuggest__results {
    position: relative;
}

.options-assign .autosuggest__results-container {
    width: 300px;
}

.options-assign .autosuggest__results-item--highlighted {
    background-color: rgba(51, 217, 178,0.2);
}

.options-assign #autosuggest-autosuggest__results {
    overflow-y: hidden;
}

/* New styles for saved card display */
.saved-card-info {
    margin-bottom: 20px;
}

.saved-card-display {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 15px;
    border: 1px solid #e9ecef;
    border-radius: 5px;
    background-color: #f8f9fa;
}

.card-info {
    display: flex;
    align-items: center;
}

.card-number {
    font-size: 1.1em;
    font-weight: bold;
    color: #007bff;
    margin-right: 10px;
}

.card-expiry {
    font-size: 0.9em;
    color: #6c757d;
}

.new-card-inputs {
    margin-top: 20px; /* Add some space between saved and new card sections */
}
</style>
